import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { chatApi, ChatChannel, ChatUserResult } from '@/api/chat';
import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';

// Hook to detect if user is actively using the app
function useActivePolling() {
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsActive(!document.hidden);
    };

    const handleFocus = () => setIsActive(true);
    const handleBlur = () => setIsActive(false);

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  return isActive;
}

export function useChannels() {
  const isActive = useActivePolling();
  const { isAuthenticated, activeOrganization } = useAuth();
  const orgId = activeOrganization?.id ?? null;

  return useQuery<ChatChannel[]>({
    queryKey: ['chat', 'channels', orgId],
    queryFn: chatApi.listChannels,
    enabled: isAuthenticated,
    refetchInterval: isActive ? 10000 : 60000,
    refetchIntervalInBackground: true,
  });
}

export function useMessages(channelId: string | undefined) {
  const isActive = useActivePolling();
  
  return useInfiniteQuery({
    queryKey: ['chat', 'messages', channelId ?? ''],
    queryFn: (context) => {
      if (!channelId) return Promise.resolve([]);
      return chatApi.getMessages(channelId, context.pageParam, 20);
    },
    getNextPageParam: (lastPage) => {
      // Return the timestamp of the oldest message (last in array after chronological sort) as the cursor
      // This is used as the 'before' parameter to fetch older messages
      if (lastPage && lastPage.length >= 20) {
        // Only fetch more if we got a full page (indicating there might be more)
        return lastPage[0].createdAt; // First message is oldest after reversal in backend
      }
      return undefined;
    },
    initialPageParam: undefined as string | undefined,
    enabled: !!channelId,
    refetchInterval: isActive ? 5000 : false,
    refetchIntervalInBackground: false,
    maxPages: 3, // Keep only 3 pages (60 messages max) in memory
  });
}

export function useSendMessage(channelId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (content: string) => chatApi.sendMessage(channelId as string, content),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat', 'messages', channelId] });
      // Also invalidate channels to update last message timestamp and unread status
      qc.invalidateQueries({ queryKey: ['chat', 'channels'] });
    },
  });
}

export function useCreateChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: chatApi.createChannel,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat', 'channels'] });
    },
  });
}

export function useUserSearch(term: string) {
  const [debounced, setDebounced] = useState(term);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(term), 250);
    return () => clearTimeout(id);
  }, [term]);

  return useQuery<ChatUserResult[]>({
    queryKey: ['chat', 'user-search', debounced],
    queryFn: () => chatApi.searchUsers(debounced),
    enabled: debounced.trim().length >= 2,
  });
}

export function useMarkChannelAsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: chatApi.markChannelAsRead,
    onSuccess: () => {
      // Invalidate channels query to refresh unread status
      qc.invalidateQueries({ queryKey: ['chat', 'channels'] });
    },
  });
}

export function useChannelMembers(channelId: string | undefined) {
  return useQuery({
    queryKey: ['chat', 'channel-members', channelId],
    queryFn: () => chatApi.getChannelMembers(channelId!),
    enabled: !!channelId,
  });
}

export function useAddMember(channelId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => chatApi.addMember(channelId!, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat', 'channel-members', channelId] });
      qc.invalidateQueries({ queryKey: ['chat', 'messages', channelId] });
      qc.invalidateQueries({ queryKey: ['chat', 'channels'] });
    },
  });
}

export function useRemoveMember(channelId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => chatApi.removeMember(channelId!, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat', 'channel-members', channelId] });
      qc.invalidateQueries({ queryKey: ['chat', 'messages', channelId] });
      qc.invalidateQueries({ queryKey: ['chat', 'channels'] });
    },
  });
}
